import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DatePipe } from '@angular/common';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import {
    ICertidaoPayload,
    ICertidaoPrefill,
    ICertidaoOrgao,
    ICertidaoDepartamento,
    ICertidaoSecretario,
} from 'src/app/interfaces/certidao.interface';

import {
    IAddCertificateSchema,
    IAddComplementaryFilesSchema,
    IAddDispachSchema,
    IAddDispachReport,
    IProccess,
    ITransmitSchema,
    IAddResponsibleSchema,
    IJoinOnProcessSchema,
    IMarkProcess,
    IProcessUserFeedbackSchema,
    ISubmitExtendProcessSchema,
    ISendRequesterConsiderationsSchema,
    IAcceptBeingResponsibleRequestBodySchema,
    ICancellationEvaluationSchema,
    IRequestToCancelProcessSchema,
    IToggleResponsibleSchema,
    IRequestToUnarchiveProcessSchema,
    IAddCorrection,
    ICorrectionDataProcessSchema
} from 'src/app/interfaces/proccess.interface';

import { GerinusService } from '../lib/gerinus.service';

import autoTable from 'jspdf-autotable';
import jsPDF from 'jspdf';
import * as QRCode from 'qrcode';
import * as dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

import { interRegular } from '../../../assets/fonts/inter/Inter-Regular-normal';
import { interSemiBold } from '../../../assets/fonts/inter/Inter-SemiBold-normal';

import { environment } from 'src/environments/environment';
import { UploadService } from './upload.service';
import { GlobalService } from '../lib/global.service';
import { processGeneratesDocumentsName } from 'src/app/Enums/commonProcessEnum';
import { ISendFileProcessPdf } from 'src/app/interfaces/processCommon.interface';

@Injectable()
export class ProcessService extends GerinusService {
    constructor(
        public http: HttpClient,
        private datePipe: DatePipe,
        private uploadService: UploadService,
        private global: GlobalService
    ) {
        super(http);
        this.entityName = 'processo';
        this.idField = 'PRC_CodigoProcesso';
        this.model = 'ProcessoModel';
    }

    public orderNumber: number;

    public async generateProcessPDF(process, hideCpf = false): Promise<ISendFileProcessPdf[]>  {
        const hideCpfIn = (text: string) => hideCpf ? this.maskCpf(text) : text;
        const doc = new jsPDF('p', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.width || doc.internal.pageSize.getWidth();
        const urlPublicProcess = environment.projectURL + '/free/processo/' + process.PRC_NumeroProcesso;

        let qrcode: string;

        QRCode.toDataURL(urlPublicProcess, (_, url) => {
            var base64Data = url.split(',')[1];
            qrcode = atob(base64Data);
        });

        const renderHeader = () => {
            const logoProtocolize = new Image();
            logoProtocolize.src = '../../assets/img/brand/logo-protocolizesga.png';
            doc.addImage(logoProtocolize, 'png', 60, 5, 95, 18);

            doc.addFileToVFS('Inter-Bold.ttf', interSemiBold);
            doc.addFont('Inter-Bold.ttf', 'Inter-Bold', 'normal');
            doc.setFont('Inter-SemiBold');

            doc.addFileToVFS('Inter-Regular.ttf', interRegular);
            doc.addFont('Inter-Regular.ttf', 'Inter-Regular', 'normal');

            doc.setFontSize(12);
            doc.setTextColor('#0A2156');
            doc.text('Sistema de Protocolo e Tramitação Eletrônica', pageWidth / 2, 30, {
                align: 'center',
            });
        };

        const renderFooter = () => {
            autoTable(doc, {
                body: [
                    [
                        {
                            content: `Situação em ${this.datePipe.transform(new Date(), "dd/MM/yyyy 'às' HH:mm:ss")}`,
                            styles: {
                                halign: 'left',
                                valign: 'middle',
                                font: 'Inter-SemiBold',
                                cellWidth: 70,
                                fontSize: 10,
                                cellPadding: {
                                    bottom: 0,
                                    top: 0,
                                    right: 0,
                                    left: 3,
                                },
                                textColor: '#0A2156',
                            },
                        },
                        {
                            content: this.getStatusDescription(process.Status),
                            styles: {
                                halign: 'left',
                                fontSize: 10,
                                cellWidth: 85,
                                cellPadding: 3,
                                textColor: '#687489',
                            },
                        },
                        {
                            rowSpan: 3,
                            styles: {
                                halign: 'center',
                                valign: 'middle',
                                fontSize: 10,
                                textColor: '#687489',
                            },
                        },
                    ],
                    [
                        {
                            content: 'Unidade Atual',
                            styles: {
                                halign: 'left',
                                valign: 'middle',
                                font: 'Inter-SemiBold',
                                cellWidth: 70,
                                fontSize: 10,
                                cellPadding: 3,
                                textColor: '#0A2156',
                            },
                        },
                        {
                            content: process.OR_Sigla + ' - ' + process.nomeDepartamentoAtual,
                            styles: {
                                halign: 'left',
                                fontSize: 10,
                                cellPadding: 3,
                                textColor: '#687489',
                            },
                        },
                    ],
                    [
                        {
                            content: 'Chave de verificação',
                            styles: {
                                halign: 'left',
                                valign: 'middle',
                                font: 'Inter-SemiBold',
                                cellWidth: 70,
                                fontSize: 10,
                                cellPadding: 3,
                                textColor: '#0A2156',
                            },
                        },
                        {
                            content: process.PRC_ChaveAutenticidade,
                            styles: {
                                halign: 'left',
                                fontSize: 10,
                                cellPadding: 3,
                                textColor: '#687489',
                            },
                        },
                    ],
                ],
                theme: 'plain',
                startY: 235,
                styles: {
                    lineWidth: 0.2,
                    lineColor: '#687489',
                    font: 'Inter-Regular',
                },
            });

            doc.addImage(qrcode, 'PNG', 171, 239, 23, 22);

            const logoSga = new Image();
            logoSga.src = '../../assets/img/brand/brasao-sga.png';

            doc.addImage(logoSga, 'png', 73, 270, 20, 18);

            const line = new Image();
            line.src = '../../assets/img/brand/line.png';

            doc.addImage(line, 'png', 100, 275, 0.2, 10);

            doc.setFont('Inter-Regular');
            doc.setFontSize(11);
            doc.setTextColor('#0A2156');

            doc.text(process.OR_Descricao || '', 107, 277, {
                maxWidth: 55
            });
        };

        renderHeader();
        

        doc.setFont('Inter-SemiBold');
        doc.setFontSize(12);
        doc.setTextColor('#0A2156');
        doc.text(`NUP: ${process.PRC_NumeroProcesso}`, pageWidth / 2, 40, {
            align: 'center',
        });

        const dataProcesso = this.datePipe.transform(process.PRC_DataRecebimento, "dd/MM/yyyy 'às' HH:mm");

        doc.setFontSize(12);
        doc.setFont('Inter-Regular');
        doc.setTextColor('#687489');
        doc.text('Recebido em: ' + dataProcesso, pageWidth / 2, 48, {
            align: 'center',
        });

        const addNewPage = (title: string) => {
            doc.addPage();

            doc.setFontSize(24);
            doc.setFont('Inter-SemiBold');
            doc.setTextColor('#0A2156');
            doc.text(title, pageWidth / 2, 45, { align: 'center' });

            renderHeader();
            renderFooter();
        };

        autoTable(doc, {
            body: [
                [
                    {
                        content: 'Solicitante:',
                        styles: {
                            halign: 'left',
                            valign: 'middle',
                            font: 'Inter-SemiBold',
                            fontSize: 10,
                            textColor: '#0a2156',
                            cellPadding: {
                                left: 4,
                                top: 2,
                                right: 4,
                            },
                        },
                    },
                ],
                [
                    {
                        content: process.SOLR_NomeRazaoSocial
                            ? `${hideCpfIn(this.formatCpfCnpj(process.SOLR_CpfCnpj))} - ${process.SOLR_NomeRazaoSocial}`
                            : `${hideCpfIn(this.formatCpfCnpj(process.PE_CPF))} - ${process.PE_Nome}`,
                        styles: {
                            halign: 'left',
                            valign: 'top',
                            fontSize: 11,
                            textColor: '#687489',
                            overflow: 'linebreak',
                            cellWidth: 180,
                            minCellHeight: 8,
                            cellPadding: {
                                left: 4,
                                top: 2,
                                right: 4,
                                bottom: 2,
                            },
                        },
                    },
                ],
            ],
            theme: 'plain',
            startY: 56,
            styles: {
                fillColor: '#f2f5f9',
            },
            margin: {
                left: 15,
                right: 15,
            },
        });

        const solicitanteFinalY = (doc as any).lastAutoTable.finalY;

        autoTable(doc, {
            body: [
                [
                    {
                        content: 'Departamento Atual:',
                        styles: {
                            halign: 'left',
                            valign: 'middle',
                            font: 'Inter-SemiBold',
                            cellWidth: 'auto',
                            fontSize: 10,
                            textColor: '#0a2156',
                            cellPadding: { left: 4, top: 2, right: 4 },
                        },
                    },
                    {
                        content: 'Assunto:',
                        styles: {
                            halign: 'left',
                            valign: 'middle',
                            font: 'Inter-SemiBold',
                            cellWidth: 'auto',
                            fontSize: 10,
                            textColor: '#0a2156',
                            cellPadding: { left: 4, top: 2, right: 4 },
                            lineWidth: { left: 1, top: 0, right: 0, bottom: 0 },
                            lineColor: '#ffffff',
                        },
                    },
                ],
                [
                    {
                        content: process.OR_Sigla + ' - ' + process.nomeDepartamentoAtual,
                        styles: {
                            halign: 'left',
                            fontSize: 12,
                            textColor: '#687489',
                            cellPadding: {
                                left: 4,
                                top: 2,
                                right: 4,
                                bottom: 2,
                            },
                        },
                    },
                    {
                        content: process.AS_Descricao,
                        styles: {
                            halign: 'left',
                            fontSize: 12,
                            textColor: '#687489',
                            cellPadding: {
                                left: 4,
                                top: 2,
                                right: 4,
                                bottom: 2,
                            },
                            lineWidth: { left: 1, top: 0, right: 0, bottom: 0 },
                            lineColor: '#ffffff',
                        },
                    },
                ],
            ],
            theme: 'plain',
            startY: solicitanteFinalY + 4,
            styles: {
                fillColor: '#f2f5f9',
            },
        });
        const departamentoFinalY = (doc as any).lastAutoTable.finalY;
        const descricaoTitleY = departamentoFinalY + 12;

        doc.setFont('Inter-SemiBold');
        doc.setFontSize(12);
        doc.setTextColor('#0A2156');
        doc.text('Descrição da solicitação', 15, descricaoTitleY);

        autoTable(doc, {
            startY: descricaoTitleY + 5,
            pageBreak: 'auto',
            rowPageBreak: 'auto',
            body: [
                [
                    {
                        content: hideCpfIn(process.SOL_Descricao || '')
                            .replace(/\r\n/g, '\n')
                            .replace(/\r/g, '\n'),
                        styles: {
                            font: 'Inter-Regular',
                            fontSize: 11,
                            textColor: '#687489',
                            overflow: 'linebreak',
                            valign: 'top',
                            cellPadding: 4,
                            cellWidth: 180,
                        },
                    },
                ],
            ],
            theme: 'plain',
            styles: {
                fillColor: '#F2F5F9',
            },
            margin: {
                top: 50,
                left: 15,
                right: 15,
                bottom: 60,
            },
        });
        renderFooter();

        if (process.PRC_CodigoProcessoApensador && process.apensador) {
            addNewPage('Apensação');

            autoTable(doc, {
                body: [
                    [
                        {
                            content: '⚠ Este processo está apensado ao processo Nº ' + process.apensador.PRC_NumeroProcesso,
                            styles: {
                                halign: 'center',
                                valign: 'middle',
                                font: 'Inter-SemiBold',
                                fontSize: 12,
                                textColor: '#856404',
                                fillColor: '#fff3cd',
                                cellPadding: 6,
                            },
                        },
                    ],
                ],
                theme: 'plain',
                startY: 65,
                styles: {
                    lineWidth: 0.3,
                    lineColor: '#ffc107',
                },
            });
        }

        const historico = process.apensadosHistorico ?? process.apensados;
        if (historico && historico.length > 0) {
            addNewPage('Processos Apensados');

            const apensadosData = historico.map((apensado) => [
                apensado.PRC_NumeroProcesso,
                apensado.assunto ?? '-',
                this.getStatusDescription(apensado.Status),
                apensado.PA_Status === 0 ? 'Desapensado' : 'Apensado',
            ]);

            autoTable(doc, {
                head: [['NUP', 'Assunto', 'Situação', 'Vínculo']],
                body: apensadosData,
                theme: 'striped',
                startY: 65,
                styles: {
                    font: 'Inter-Regular',
                    minCellHeight: 8,
                    valign: 'middle',
                    halign: 'left',
                },
                alternateRowStyles: {
                    fillColor: '#F2F5F9',
                },
                headStyles: {
                    fillColor: '#417bff',
                    font: 'Inter-SemiBold',
                    minCellHeight: 8,
                    valign: 'middle',
                    halign: 'left',
                },
                didParseCell: (data) => {
                    if (data.column.index === 3 && data.section === 'body') {
                        if (data.cell.raw === 'Desapensado') {
                            data.cell.styles.textColor = '#856404';
                        }
                    }
                },
            });
        }

        if (process.despachos && process.despachos.length > 0) {
            addNewPage('Folha de Despacho');

            const despachoData = process.despachos.map((despacho) => [
                dayjs(despacho.created_at).format('DD/MM/YYYY'),
                hideCpfIn(despacho.PDES_Descricao),
            ]);

            const formattedData = despachoData.map(row =>
                row.map(cell => cell
                    .replace(/\t/g, '    ')
                )
            );

            autoTable(doc, {
                head: [['Data', 'Despacho']],
                body: formattedData,
                theme: 'striped',
                startY: 65,
                styles: {
                    font: 'Inter-Regular',
                    minCellHeight: 8,
                    valign: 'middle',
                    halign: 'left',
                },
                alternateRowStyles: {
                    fillColor: '#F2F5F9',
                },
                headStyles: {
                    fillColor: '#417bff',
                    font: 'Inter-SemiBold',
                    minCellHeight: 8,
                    valign: 'middle',
                    halign: 'left',
                },
            });
        }


        addNewPage('Folha de Registros');

        const historicosData = (process.historicos || []).map((historico) => [
            this.datePipe.transform(historico.PRCH_Data, "dd/MM/yyyy 'às' HH:mm:ss"),
            hideCpfIn(historico.PRCH_Historico),
        ]);

        autoTable(doc, {
            head: [['Data', 'Histórico']],
            body: historicosData,
            theme: 'striped',
            startY: 65,
            styles: {
                font: 'Inter-Regular',
                minCellHeight: 8,
                valign: 'middle',
                halign: 'left',
            },
            alternateRowStyles: {
                fillColor: '#F2F5F9',
            },
            headStyles: {
                fillColor: '#417bff',
                font: 'Inter-SemiBold',
                minCellHeight: 8,
                valign: 'middle',
                halign: 'left',
            },
        });

        const listBlobPdf = [
            { name: processGeneratesDocumentsName.PROCESS, file: doc.output('blob') }, 
            { name: processGeneratesDocumentsName.DOCUMENT_OF_PROCESS, file: this.generatePageDocumentCover(process, "DOCUMENTOS DO PROCESSO", "#0A2156", qrcode)},
            { name: processGeneratesDocumentsName.ADDITIONAL_DOCUMENTS, file: this.generatePageDocumentCover(process, "DOCUMENTOS COMPLEMENTARES", "#0A2156", qrcode, 28)},
            { name: processGeneratesDocumentsName.DISPATCH_DOCUMENTO, file: this.generatePageDocumentCover(process, "PARECER DE DESPACHO", "#0A2156", qrcode)},
            { name: processGeneratesDocumentsName.CERTIFICATE, file: this.generatePageDocumentCover(process, "CERTIDÃO", "#0A2156", qrcode)},
        ]

        return listBlobPdf;
    }

    public generatePageDocumentCover(process, text: string, color: string,  qrcode, fontSize = 36): Blob {
        const doc = new jsPDF('p', 'mm', 'a4');
        const pageWidth = doc.internal.pageSize.width || doc.internal.pageSize.getWidth();

        const logoProtocolize = new Image();
        logoProtocolize.src = '../../assets/img/brand/logo-protocolizesga.png';
        doc.addImage(logoProtocolize, 'png', 60, 5, 95, 18);

        doc.addFileToVFS('Inter-Bold.ttf', interSemiBold);
        doc.addFont('Inter-Bold.ttf', 'Inter-Bold', 'normal');
        doc.setFont('Inter-SemiBold');

        doc.addFileToVFS('Inter-Regular.ttf', interRegular);
        doc.addFont('Inter-Regular.ttf', 'Inter-Regular', 'normal');

        doc.setFontSize(12);
        doc.setTextColor('#0A2156');
        doc.text('Sistema de Protocolo e Tramitação Eletrônica', pageWidth / 2, 30, {
            align: 'center',
        });

        doc.setFontSize(fontSize);
        doc.setFont('Inter-SemiBold');
        doc.setTextColor(color);
        doc.text(text, pageWidth / 2, 140, { align: 'center' });

        autoTable(doc, {
            body: [
                [
                    {
                        content: `Situação em ${this.datePipe.transform(new Date(), "dd/MM/yyyy 'às' HH:mm:ss")}`,
                        styles: {
                            halign: 'left',
                            valign: 'middle',
                            font: 'Inter-SemiBold',
                            cellWidth: 70,
                            fontSize: 10,
                            cellPadding: {
                                bottom: 0,
                                top: 0,
                                right: 0,
                                left: 3,
                            },
                            textColor: '#0A2156',
                        },
                    },
                    {
                        content: this.getStatusDescription(process.Status),
                        styles: {
                            halign: 'left',
                            fontSize: 10,
                            cellWidth: 85,
                            cellPadding: 3,
                            textColor: '#687489',
                        },
                    },
                    {
                        rowSpan: 3,
                        styles: {
                            halign: 'center',
                            valign: 'middle',
                            fontSize: 10,
                            textColor: '#687489',
                        },
                    },
                ],
                [
                    {
                        content: 'Unidade Atual',
                        styles: {
                            halign: 'left',
                            valign: 'middle',
                            font: 'Inter-SemiBold',
                            cellWidth: 70,
                            fontSize: 10,
                            cellPadding: 3,
                            textColor: '#0A2156',
                        },
                    },
                    {
                        content: process.OR_Sigla + '-' + process.nomeDepartamentoAtual,
                        styles: {
                            halign: 'left',
                            fontSize: 10,
                            cellPadding: 3,
                            textColor: '#687489',
                        },
                    },
                ],
            ],
            theme: 'plain',
            startY: 240,
            styles: {
                lineWidth: 0.2,
                lineColor: '#687489',
                font: 'Inter-Regular',
            },
        });

        doc.addImage(qrcode, 'PNG', 171, 241, 23, 22);

        const logoSga = new Image();
        logoSga.src = '../../assets/img/brand/brasao-sga.png';

        doc.addImage(logoSga, 'png', 73, 270, 20, 18);

        const line = new Image();
        line.src = '../../assets/img/brand/line.png';

        doc.addImage(line, 'png', 100, 275, 0.2, 10);

        doc.setFont('Inter-Regular');
        doc.setFontSize(11);
        doc.setTextColor('#0A2156');

        doc.text(process.OR_Descricao || '', 107, 277, {
            maxWidth: 55
        });

        return doc.output('blob');
    }

    getStatusDescription(processStatus: number): string {
        const statusDescriptions = {
            1: 'Novo',
            2: 'Em Andamento',
            3: 'Arquivado',
            4: 'Concluído',
            5: 'Aguardando ajuste do cidadão',
            6: 'Vencido',
            7: 'Anulado',
            8: 'Respondido',
            9: 'Vencido sem Análise',
            10: 'Aguardando restituição',
            11: 'Pendente de Atribuição',
            12: 'Aguardando pagamento',
            13: 'Apensado',
            14: 'Aguardando assinatura do Secretário'
        };

        return statusDescriptions[processStatus];
    }

    /**
     * Classes do badge (ui-badge, em request-ui.scss) de cada status de processo: a cor
     * fica só no ponto, como no Untitled. Mesma ordem de getStatusDescription.
     */
    getStatusBadge(processStatus: number, size: 'sm' | 'md' | 'lg' = 'sm'): string {
        const colors = {
            1: 'brand',
            2: 'blue-light',
            3: 'gray',
            4: 'success',
            5: 'warning',
            6: 'orange',
            7: 'error',
            8: 'indigo',
            9: 'orange',
            10: 'blue',
            11: 'gray-blue',
            12: 'warning',
            13: 'gray',
            14: 'purple',
        };

        return `ui-badge ui-badge--${size} ui-badge--${colors[processStatus] || 'gray'}`;
    }

    getStatusResponsibleDescription(processStatus: number): string {
        const statusDescriptions = {
            0: 'Aceite Pendente',
            1: 'Aceito',
            2: 'Recusado'
        };

        return statusDescriptions[processStatus];
    }

    publicProcess(id: string) {
        return this.http.get(this.apiURL() + '/api/free/process/' + id);
    }

    buscarParaApensar(processId: number, data: { nup: string; PE_CodigoPessoa: number }): Observable<any> {
        return this.http.post(this.apiURL() + `/api/process/${processId}/buscar-para-apensar`, data, this.headers()).pipe(catchError(this.handleError));
    }

    apensarProcesso(processId: number, data: { PA_CodigoProcessoApensado: number; PA_Justificativa: string; PE_CodigoPessoa: number; PE_Nome: string }): Observable<any> {
        return this.http.post(this.apiURL() + `/api/process/${processId}/apensar`, data, this.headers()).pipe(catchError(this.handleError));
    }

    desapensarProcesso(processId: number, apensacaoId: number, data: { PA_JustificativaDesapensacao: string; PE_Nome: string }): Observable<any> {
        return this.http.post(this.apiURL() + `/api/process/${processId}/desapensar/${apensacaoId}`, data, this.headers()).pipe(catchError(this.handleError));
    }

    async conclusionProccess(processo: IProccess) {
        const doc = new jsPDF('p', 'mm', 'a4');

        const addNewPage = (title: string) => {
            doc.addPage();

            doc.setFontSize(24);
            doc.setFont('Inter-SemiBold');
            doc.setTextColor('#0A2156');
            doc.text(title, pageWidth / 2, 45, { align: 'center' });

            renderHeader();
            renderFooter();
        };

        const renderHeader = () => {
            const logoProtocolize = new Image();
            logoProtocolize.src = '../../assets/img/brand/logo-protocolizesga.png';
            doc.addImage(logoProtocolize, 'png', 60, 5, 95, 18);

            doc.addFileToVFS('Inter-Bold.ttf', interSemiBold);
            doc.addFont('Inter-Bold.ttf', 'Inter-Bold', 'normal');
            doc.setFont('Inter-SemiBold');

            doc.addFileToVFS('Inter-Regular.ttf', interRegular);
            doc.addFont('Inter-Regular.ttf', 'Inter-Regular', 'normal');

            doc.setFontSize(12);
            doc.setTextColor('#0A2156');
        };

        const renderFooter = () => {
            const logoSga = new Image();
            logoSga.src = '../../assets/img/brand/logo_sga_horizontal.png';
            doc.addImage(logoSga, 'png', 50, 270, 45, 20);

            const line = new Image();
            line.src = '../../assets/img/brand/line.png';
            doc.addImage(line, 'png', 100, 275, 0.2, 10);

            doc.setFont('Inter-Regular');
            doc.setFontSize(12);
            doc.setTextColor('#0A2156');
            doc.text(processo.OR_Descricao, 107, 279, { maxWidth: 40 });
        };

        renderHeader();

        const historicosData = processo.historicos.map((historico) => [
            this.datePipe.transform(historico.PRCH_Data, "dd/MM/yyyy 'às' HH:mm:ss"),
            historico.PRCH_Historico,
        ]);

        autoTable(doc, {
            head: [['Data', 'Histórico']],
            body: historicosData,
            theme: 'striped',
            startY: 65,
            styles: {
                font: 'Inter-Regular',
                minCellHeight: 12,
                valign: 'middle',
                halign: 'left',
            },
            alternateRowStyles: {
                fillColor: '#F2F5F9',
            },
            headStyles: {
                fillColor: '#417bff',
                font: 'Inter-SemiBold',
                minCellHeight: 12,
                valign: 'middle',
                halign: 'left',
            },
        });

        const pageWidth = doc.internal.pageSize.width || doc.internal.pageSize.getWidth();

        await addNewPage('Folha de Despacho');

        autoTable(doc, {
            body: [
                [
                    {
                        content: 'Despacho',
                        styles: {
                            halign: 'left',
                            valign: 'middle',
                            font: 'Inter-SemiBold',
                            fontSize: 10,
                            cellPadding: 4,
                        },
                    },
                ],
            ],
            theme: 'plain',
            startY: 100,
            styles: {
                lineWidth: 0.2,
                lineColor: '#687489',
                font: 'Inter-Regular',
            },
        });

        return doc.save(`Processo N° ${processo.PRC_NumeroProcesso}.pdf`);
    }

    transmit(processId: number, data: ITransmitSchema): Observable<any> {
        return this.http.post(this.apiURL() + '/api/process/' + processId + '/transmit', data, this.headers())
            .pipe(
                catchError(this.handleError)
            )
    }

    addDispatch(processId: number, data: IAddDispachSchema): Observable<any> {
        return this.http.post(this.apiURL() + '/api/process/' + processId + '/add-dispatch', data, this.headers())
            .pipe(
                catchError(this.handleError)
            )
    }

    addDispatchReport(processId: number, data: IAddDispachReport): Observable<any> {
        return this.http.post(this.apiURL() + '/api/process/' + processId + '/add-dispatch-report', data, this.headers())
            .pipe(
                catchError(this.handleError)
            )
    }

    createParecerDraft(processId: number, data: {
        numeroParecer?: string | null;
        ementa: string;
        conteudoHtml: string;
        assinaturaSecretarioObrigatoria: boolean;
        nomeSecretario?: string | null;
        userId: number;
    }): Observable<any> {
        return this.http.post(this.apiURL() + '/api/process/' + processId + '/parecer-despacho', data, this.headers())
            .pipe(catchError(this.handleError));
    }

    updateParecerDraft(processId: number, parecerId: number, data: {
        numeroParecer?: string | null;
        ementa?: string;
        conteudoHtml?: string;
        assinaturaSecretarioObrigatoria?: boolean;
        nomeSecretario?: string | null;
    }): Observable<any> {
        return this.http.put(this.apiURL() + '/api/process/' + processId + '/parecer-despacho/' + parecerId, data, this.headers())
            .pipe(catchError(this.handleError));
    }

    getParecerSecretarios(processId: number): Observable<{ secretarios: string[] }> {
        return this.http.get<{ secretarios: string[] }>(
            this.apiURL() + '/api/process/' + processId + '/parecer-despacho/secretarios',
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    previewParecer(processId: number, data: {
        numeroParecer?: string | null;
        ementa: string;
        conteudoHtml: string;
        assinaturaSecretarioObrigatoria: boolean;
        nomeSecretario?: string | null;
        userId?: number;
    }): Observable<Blob> {
        return this.http.post(
            this.apiURL() + '/api/process/' + processId + '/parecer-despacho/preview',
            data,
            { ...this.headers(), responseType: 'blob' }
        ).pipe(catchError(this.handleError));
    }

    prepareParecerSign(
        processId: number,
        parecerId: number,
    ): Observable<{
        pdfBase64: string;
        fileName: string;
        pageCount: number;
        signaturePosition: { page: number; xPercentage: number; yPercentage: number } | null;
        secretarioSignaturePosition: { page: number; xPercentage: number; yPercentage: number } | null;
    }> {
        return this.http.post<{
            pdfBase64: string;
            fileName: string;
            pageCount: number;
            signaturePosition: { page: number; xPercentage: number; yPercentage: number } | null;
            secretarioSignaturePosition: { page: number; xPercentage: number; yPercentage: number } | null;
        }>(
            this.apiURL() + '/api/process/' + processId + '/parecer-despacho/' + parecerId + '/prepare-sign',
            {},
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    finalizeParecerSign(
        processId: number,
        parecerId: number,
        data: {
            fileCloudFlare: string;
            fileName: string;
            secretarioSignaturePosition?: { page: number; xPercentage: number; yPercentage: number } | null;
        },
    ): Observable<any> {
        return this.http.post(
            this.apiURL() + '/api/process/' + processId + '/parecer-despacho/' + parecerId + '/finalize-sign',
            data,
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    discardParecerDraft(processId: number, parecerId: number): Observable<any> {
        return this.http.delete(
            this.apiURL() + '/api/process/' + processId + '/parecer-despacho/' + parecerId,
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    // ------------------------------------------------------------------
    // Certidão Digital (SEMURB)
    // ------------------------------------------------------------------

    getCertidaoPrefill(processId: number): Observable<ICertidaoPrefill> {
        return this.http.get<ICertidaoPrefill>(
            this.apiURL() + '/api/process/' + processId + '/certidao/prefill',
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    getCertidaoOrgaos(processId: number): Observable<{ orgaos: ICertidaoOrgao[]; orgaoProcesso: number | null }> {
        return this.http.get<{ orgaos: ICertidaoOrgao[]; orgaoProcesso: number | null }>(
            this.apiURL() + '/api/process/' + processId + '/certidao/orgaos',
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    getCertidaoDepartamentos(processId: number, organizationId?: number): Observable<{ departamentos: ICertidaoDepartamento[] }> {
        const query = organizationId ? '?organizationId=' + organizationId : '';
        return this.http.get<{ departamentos: ICertidaoDepartamento[] }>(
            this.apiURL() + '/api/process/' + processId + '/certidao/departamentos' + query,
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    getCertidaoSecretarios(processId: number, departmentId?: number, organizationId?: number): Observable<{ secretarios: ICertidaoSecretario[] }> {
        const params: string[] = [];
        if (departmentId) params.push('departmentId=' + departmentId);
        if (organizationId) params.push('organizationId=' + organizationId);
        const query = params.length ? '?' + params.join('&') : '';
        return this.http.get<{ secretarios: ICertidaoSecretario[] }>(
            this.apiURL() + '/api/process/' + processId + '/certidao/secretarios' + query,
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    createCertidaoDraft(processId: number, data: ICertidaoPayload & { userId: number }): Observable<any> {
        return this.http.post(
            this.apiURL() + '/api/process/' + processId + '/certidao',
            data,
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    updateCertidaoDraft(processId: number, certidaoId: number, data: ICertidaoPayload): Observable<any> {
        return this.http.put(
            this.apiURL() + '/api/process/' + processId + '/certidao/' + certidaoId,
            data,
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    discardCertidaoDraft(processId: number, certidaoId: number): Observable<any> {
        return this.http.delete(
            this.apiURL() + '/api/process/' + processId + '/certidao/' + certidaoId,
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    cancelCertidao(processId: number, certidaoId: number, justificativa?: string): Observable<any> {
        return this.http.post(
            this.apiURL() + '/api/process/' + processId + '/certidao/' + certidaoId + '/cancel',
            { justificativa },
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    previewCertidao(processId: number, data: ICertidaoPayload & { userId?: number }): Observable<Blob> {
        return this.http.post(
            this.apiURL() + '/api/process/' + processId + '/certidao/preview',
            data,
            { ...this.headers(), responseType: 'blob' }
        ).pipe(catchError(this.handleError));
    }

    prepareCertidaoSign(processId: number, certidaoId: number): Observable<{
        pdfBase64: string;
        fileName: string;
        pageCount: number;
        signaturePosition: { page: number; xPercentage: number; yPercentage: number } | null;
        secretarioSignaturePosition: { page: number; xPercentage: number; yPercentage: number } | null;
    }> {
        return this.http.post<any>(
            this.apiURL() + '/api/process/' + processId + '/certidao/' + certidaoId + '/prepare-sign',
            {},
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    finalizeCertidaoSign(
        processId: number,
        certidaoId: number,
        data: {
            fileCloudFlare: string;
            fileName: string;
            secretarioSignaturePosition?: { page: number; xPercentage: number; yPercentage: number } | null;
        },
    ): Observable<any> {
        return this.http.post(
            this.apiURL() + '/api/process/' + processId + '/certidao/' + certidaoId + '/finalize-sign',
            data,
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    sendCertidaoToSecretary(processId: number, certidaoId: number): Observable<any> {
        return this.http.post(
            this.apiURL() + '/api/process/' + processId + '/certidao/' + certidaoId + '/send-to-secretary',
            {},
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    /** "Cancelar envio": a certidão volta a ser rascunho e sai da fila do(a) secretário(a). */
    recallCertidaoFromSecretary(processId: number, certidaoId: number): Observable<any> {
        return this.http.post(
            this.apiURL() + '/api/process/' + processId + '/certidao/' + certidaoId + '/recall-from-secretary',
            {},
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    prepareCertidaoSecretarySign(certidaoId: number): Observable<{
        pdfBase64: string;
        fileName: string;
        pageCount: number;
        signaturePosition: { page: number; xPercentage: number; yPercentage: number } | null;
    }> {
        return this.http.post<any>(
            this.apiURL() + '/api/certidao/' + certidaoId + '/prepare-secretary-sign',
            {},
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    finalizeCertidaoSecretarySign(
        certidaoId: number,
        data: { fileCloudFlare: string; fileName: string; userId: number },
    ): Observable<any> {
        return this.http.post(
            this.apiURL() + '/api/certidao/' + certidaoId + '/finalize-secretary-sign',
            data,
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    listCertidaoPendingForSecretary(
        userId: number,
        page: number = 1,
        perPage: number = 10,
        filters?: { numeroProcesso?: string; interessado?: string; ementa?: string; dataInicio?: string; dataFim?: string },
    ): Observable<{ data: any[]; meta: any }> {
        const params = this.buildSecretaryQueryParams(userId, page, perPage, filters);
        return this.http.get<{ data: any[]; meta: any }>(
            this.apiURL() + '/api/certidao/secretario/pending?' + params,
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    listCertidaoSignedHistoryForSecretary(
        userId: number,
        page: number = 1,
        perPage: number = 10,
        filters?: { numeroProcesso?: string; interessado?: string; ementa?: string; dataInicio?: string; dataFim?: string },
    ): Observable<{ data: any[]; meta: any }> {
        const params = this.buildSecretaryQueryParams(userId, page, perPage, filters);
        return this.http.get<{ data: any[]; meta: any }>(
            this.apiURL() + '/api/certidao/secretario/history?' + params,
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    listCertidaoAllForSecretary(
        userId: number,
        page: number = 1,
        perPage: number = 10,
        filters?: { numeroProcesso?: string; interessado?: string; ementa?: string; dataInicio?: string; dataFim?: string },
    ): Observable<{ data: any[]; meta: any }> {
        const params = this.buildSecretaryQueryParams(userId, page, perPage, filters);
        return this.http.get<{ data: any[]; meta: any }>(
            this.apiURL() + '/api/certidao/secretario/all?' + params,
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    listParecerPendingForSecretary(
        userId: number,
        page: number = 1,
        perPage: number = 10,
        filters?: { numeroProcesso?: string; interessado?: string; ementa?: string; dataInicio?: string; dataFim?: string },
    ): Observable<{ data: any[]; meta: any }> {
        const params = this.buildSecretaryQueryParams(userId, page, perPage, filters);
        return this.http.get<{ data: any[]; meta: any }>(
            this.apiURL() + '/api/parecer-despacho/secretario/pending?' + params,
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    listParecerSignedHistoryForSecretary(
        userId: number,
        page: number = 1,
        perPage: number = 10,
        filters?: { numeroProcesso?: string; interessado?: string; ementa?: string; dataInicio?: string; dataFim?: string },
    ): Observable<{ data: any[]; meta: any }> {
        const params = this.buildSecretaryQueryParams(userId, page, perPage, filters);
        return this.http.get<{ data: any[]; meta: any }>(
            this.apiURL() + '/api/parecer-despacho/secretario/history?' + params,
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    listParecerAllForSecretary(
        userId: number,
        page: number = 1,
        perPage: number = 10,
        filters?: { numeroProcesso?: string; interessado?: string; ementa?: string; dataInicio?: string; dataFim?: string },
    ): Observable<{ data: any[]; meta: any }> {
        const params = this.buildSecretaryQueryParams(userId, page, perPage, filters);
        return this.http.get<{ data: any[]; meta: any }>(
            this.apiURL() + '/api/parecer-despacho/secretario/all?' + params,
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    private buildSecretaryQueryParams(
        userId: number,
        page: number,
        perPage: number,
        filters?: { numeroProcesso?: string; interessado?: string; ementa?: string; dataInicio?: string; dataFim?: string },
    ): string {
        const params = new URLSearchParams();
        params.set('userId', String(userId));
        params.set('page', String(page));
        params.set('perPage', String(perPage));
        if (filters?.numeroProcesso) params.set('numeroProcesso', filters.numeroProcesso);
        if (filters?.interessado) params.set('interessado', filters.interessado);
        if (filters?.ementa) params.set('ementa', filters.ementa);
        if (filters?.dataInicio) params.set('dataInicio', filters.dataInicio);
        if (filters?.dataFim) params.set('dataFim', filters.dataFim);
        return params.toString();
    }

    prepareParecerSecretarySign(parecerId: number): Observable<{
        pdfBase64: string;
        fileName: string;
        pageCount: number;
        signaturePosition: { page: number; xPercentage: number; yPercentage: number } | null;
    }> {
        return this.http.post<{
            pdfBase64: string;
            fileName: string;
            pageCount: number;
            signaturePosition: { page: number; xPercentage: number; yPercentage: number } | null;
        }>(
            this.apiURL() + '/api/parecer-despacho/' + parecerId + '/prepare-secretary-sign',
            {},
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    finalizeParecerSecretarySign(parecerId: number, data: { fileCloudFlare: string; fileName: string; userId: number }): Observable<any> {
        return this.http.post(
            this.apiURL() + '/api/parecer-despacho/' + parecerId + '/finalize-secretary-sign',
            data,
            this.headers()
        ).pipe(catchError(this.handleError));
    }

    addCorrection(processId: number, data: IAddCorrection): Observable<any> {
        return this.http.post(this.apiURL() + '/api/process/' + processId + '/add-correction', data, this.headers())
            .pipe(
                catchError(this.handleError)
            )
    }

    addComplementaryFiles(processId: number, data: IAddComplementaryFilesSchema): Observable<any> {
        return this.http.post(this.apiURL() + '/api/process/' + processId + '/add-complementary-files', data, this.headers())
            .pipe(
                catchError(this.handleError)
            )
    }

    addCertificate(processId: number, data: IAddCertificateSchema): Observable<any> {
        return this.http.post(this.apiURL() + '/api/process/' + processId + '/add-certificate', data, this.headers())
            .pipe(
                catchError(this.handleError)
            )
    }

    sendRequesterCorrection(processId: number, data: any): Observable<any> {
        return this.http.post(this.apiURL() + '/api/process/' + processId + '/send-requester-correction', data, this.headers())
            .pipe(
                catchError(this.handleError)
            )
    }

    sendToCorrection(processId: number, data: ICorrectionDataProcessSchema): Observable<any> {
        return this.http.post(this.apiURL() + '/api/process/' + processId + '/send-to-correction', data, this.headers())
            .pipe(
                catchError(this.handleError)
            )
    }

    markProcessAsCompleted(processId: number, data: IMarkProcess): Observable<any> {
        return this.http.post(this.apiURL() + '/api/process/' + processId + '/mark-as-completed', data, this.headers())
            .pipe(
                catchError(this.handleError)
            )
    }

    markProcessAsArchived(processId: number, data: IMarkProcess): Observable<any> {
        return this.http.post(this.apiURL() + '/api/process/' + processId + '/mark-as-archived', data, this.headers())
            .pipe(
                catchError(this.handleError)
            )
    }

    markAsUnarchived(processId: number, data: IMarkProcess): Observable<any> {
        return this.http.post(this.apiURL() + '/api/process/' + processId + '/mark-as-unarchived', data, this.headers())
            .pipe(
                catchError(this.handleError)
            )

    }

    markTramitacaoNoteAsRead(processId: number, noteId: number): Observable<any> {
        return this.http.post(this.apiURL() + '/api/process/' + processId + '/tramitacao-notes/' + noteId + '/read', {}, this.headers())
            .pipe(
                catchError(this.handleError)
            )
    }

    addResponsible(processId: number, data: IAddResponsibleSchema): Observable<any> {
        return this.http.post(this.apiURL() + '/api/process/' + processId + '/add-responsible', data, this.headers())
            .pipe(
                catchError(this.handleError)
            )
    }

    toggleResponsible(processId: number, data: IToggleResponsibleSchema): Observable<any> {
        return this.http.put(this.apiURL() + '/api/process/' + processId + '/toggle-responsible', data, this.headers())
            .pipe(
                catchError(this.handleError)
            );
    }

    
    join(processId: number, data: IJoinOnProcessSchema): Observable<any> {
        return this.http.post(this.apiURL() + '/api/process/' + processId + '/join', data, this.headers())
            .pipe(
                catchError(this.handleError)
            )
    }

    search(data: any): Observable<any> {
        return this.http
            .post(this.apiURL() + '/api/free/process/search', data, this.headers())
            .pipe(catchError(this.handleError));
    }

    getTransmissions(processId: number): Observable<any> {
        return this.http.get(this.apiURL() + '/api/free/process/' + processId + '/get-transmissions', this.headers())
            .pipe(
                catchError(this.handleError)
            )
    }

    submitUserFeedback(data: IProcessUserFeedbackSchema): Observable<any> {
        return this.http.post(this.apiURL() + '/api/free/process/add-user-feedback', data, this.headers())
            .pipe(
                catchError(this.handleError)
            )
    }

    listProcessFeedback(processId: number): Observable<any> {
        return this.http.get(this.apiURL() + `/api/free/process/${processId}/list-user-feedbacks/`, this.headers())
            .pipe(
                catchError(this.handleError)
            )
    }

    submitExtendsProcess(data: ISubmitExtendProcessSchema): Observable<any> {
        return this.http.post(this.apiURL() + '/api/process/extend-process', data, this.headers())
    }

    updateStatusToEvaluation(processId: number) {
        return this.http.post(this.apiURL() + '/api/process/' + processId + '/update-to-evaluation', null, this.headers())
            .pipe(
                catchError(this.handleError)
            )
    }

    addRequestToUnarchive(processId: number, data: IMarkProcess): Observable<any> {
        return this.http.post(`${this.apiURL()}/api/process/${processId}/add-request-to-unarchive`, data, this.headers())
            .pipe(catchError(this.handleError));
    }
    

    addRequestToCancel(processId: number, data: IRequestToCancelProcessSchema): Observable<any> {
        return this.http.post(this.apiURL() + '/api/process/' + processId + '/add-request-to-cancel', { data }, this.headers())
            .pipe(
                catchError(this.handleError)
            )
    }

    confirmCancellationProcess(processId: number, data: ICancellationEvaluationSchema): Observable<any> {
        return this.http.post(this.apiURL() + '/api/process/' + processId + '/confirm-cancellation', { data }, this.headers())
            .pipe(
                catchError(this.handleError)
            )
    }

    returnToSender(processId: number, data: any): Observable<any> {        
        return this.http.post(
            this.apiURL() + '/api/process/' + processId + '/return-transmitter',
            data,
            this.headers()
        ).pipe(
            catchError(this.handleError)
        );
    }

    refuseCancellationProcess(processId: number, data: ICancellationEvaluationSchema): Observable<any> {
        return this.http.post(this.apiURL() + '/api/process/' + processId + '/refuse-cancellation', { data }, this.headers())
            .pipe(
                catchError(this.handleError)
            )
    }

    getResponsibleRequests(processId: number) {
        return this.http.get(this.apiURL() + '/api/process/' + processId + '/get-responsible-requests', this.headers());
    }

    sendAcceptBeingResponsible(processId: number, data: IAcceptBeingResponsibleRequestBodySchema) {
        return this.http.post(this.apiURL() + '/api/process/' + processId + '/accept-being-responsible', { data }, this.headers());
    }
    
    cancelSeemDispatch(processId: number, data: any) {
        return this.http.post(this.apiURL() + '/api/process/' + processId + '/cancel-seem-dispatch', { data }, this.headers())
            .pipe(
                catchError(this.handleError)
            )
    }

    getAllCorrections(processId: number) {
        return this.http.get(this.apiURL() + '/api/process/' + processId + '/get-corrections', this.headers())
    }
    
    cancelComplementaryFiles(processId: number, data: any) {
        return this.http.put(this.apiURL() + '/api/process/' + processId + '/cancel-complementary-files', { data }, this.headers())
            .pipe(
                catchError(this.handleError)
            )
    }

    adminRemoveHistory(processId: number, historyId: number): Observable<any> {
        return this.http.delete(this.apiURL() + '/api/process/' + processId + '/admin/history/' + historyId, this.headers())
            .pipe(catchError(this.handleError));
    }

    adminRemoveDispatch(processId: number, dispatchId: number): Observable<any> {
        return this.http.delete(this.apiURL() + '/api/process/' + processId + '/admin/dispatch/' + dispatchId, this.headers())
            .pipe(catchError(this.handleError));
    }

    adminRemoveConsideration(processId: number, considerationId: number): Observable<any> {
        return this.http.delete(this.apiURL() + '/api/process/' + processId + '/admin/consideration/' + considerationId, this.headers())
            .pipe(catchError(this.handleError));
    }

    adminRemoveParecerDespacho(processId: number, parecerId: number): Observable<any> {
        return this.http.delete(this.apiURL() + '/api/process/' + processId + '/admin/parecer-despacho/' + parecerId, this.headers())
            .pipe(catchError(this.handleError));
    }

    adminRemoveCertificate(processId: number, certificateId: number): Observable<any> {
        return this.http.delete(this.apiURL() + '/api/process/' + processId + '/admin/certificate/' + certificateId, this.headers())
            .pipe(catchError(this.handleError));
    }

    adminRemoveComplementaryFile(processId: number, attachmentId: number): Observable<any> {
        return this.http.delete(this.apiURL() + '/api/process/' + processId + '/admin/complementary-file/' + attachmentId, this.headers())
            .pipe(catchError(this.handleError));
    }
    unarchivingProcessAutomaticArchived(processId: number) {
        return this.http.put(this.apiURL() + '/api/process/' + processId + '/unarchiving-process-automatic-archived', {}, this.headers())
    } 
    
    associarDam(processId: number, damId: string, damSituacao: string): Observable<any> {
        const data = { damId, damSituacao };
        return this.http.post(this.apiURL() + '/api/process/' + processId + '/associar-dam', data, this.headers());
    }

    removeDam(processId: number, damId: number): Observable<any> {
        return this.http.delete(this.apiURL() + '/api/process/' + processId + '/remove-dam/'+damId, this.headers())
            .pipe(
                catchError(this.handleError)
            );
    }

    reopenProcess(processId: number) {
        return this.http.get(this.apiURL() + '/api/process/' + processId + '/reopen-process', this.headers());
    }

    private handleError(error: any): Observable<never> {
        console.error('An error occurred:', error);
        return throwError(() => new Error('Something went wrong; please try again later.'));
    }

    public getUnassignedProcesses() {
        return this.http.get(this.apiURL() + '/api/process/get-unassigned-processes/', this.headers());
    }

    public mergedDocumentInPdf(files: ISendFileProcessPdf[], processId: number, processNumber: string, org: string) {
        const formData = new FormData()
        for (const fileElement of files) {
            formData.append(fileElement.name + ".pdf", fileElement.file)
        }   
        return this.http.post(this.apiURL() + '/api/pdf/process/' + processId + '/' + processNumber + "/" + org, formData ,  { ...this.headers(true), responseType: 'blob', reportProgress: true, observe: 'events'}); 
    }

    public generateListReportPDF(title: string, condition: string) {
        const formData = new FormData();
        formData.append('title', title);
        formData.append('condition', condition);
    
        return this.http.post(
            this.apiURL() + '/api/pdf/process-report-list', 
            formData, 
            { 
                ...this.headers(true), 
                responseType: 'blob', 
                reportProgress: true, 
                observe: 'events' 
            }
        );
    }

    associarNegociacao(processId: number, negociacaoId: string): Observable<any> {
        const data = { negociacaoId };
        return this.http.post(this.apiURL() + '/api/process/' + processId + '/associar-negociacao', data, this.headers())
            .pipe(
                catchError(this.handleError)
            );
    }

    removeNegociacao(processId: number, negociacaoId: number): Observable<any> {
        return this.http.delete(this.apiURL() + '/api/process/' + processId + '/remove-negociacao/'+negociacaoId, this.headers())
            .pipe(
                catchError(this.handleError)
            );
    }

    atualizarUrlTermo(processId: number, codigoNegociacaoPdf: number, urlCloudflare: string, person: number): Observable<any> {
        const data = { urlCloudflare, person };
        return this.http.post(this.apiURL() + '/api/process/' + processId + '/update-url-termo/' + codigoNegociacaoPdf , data, this.headers())
            .pipe(
                catchError(this.handleError)
            );
    }

    associarAlvara(processId: number, tipo: string, numero: number, exercicio: number): Observable<any> {
        const data = { tipo, numero, exercicio };
        return this.http.post(this.apiURL() + '/api/process/' + processId + '/associar-alvara', data, this.headers())
            .pipe(
                catchError(this.handleError)
            );
    }

    removeAlvara(processId: number, tipo: string, numero: number, exercicio: number): Observable<any> {
        const data = { tipo, numero, exercicio };
        return this.http.post(this.apiURL() + '/api/process/' + processId + '/remove-alvara', data, this.headers())
            .pipe(
                catchError(this.handleError)
            );
    }

    atualizarUrlAlvaraSanitario(processId: number, codigoAlvaraPdf: number, urlCloudflare: string): Observable<any> {
        const data = { urlCloudflare };
        return this.http.post(this.apiURL() + '/api/process/' + processId + '/update-url-alvara-sanitario/' + codigoAlvaraPdf , data, this.headers())
            .pipe(
                catchError(this.handleError)
            );
    }

    associarCertificado(processId: number, numero: number, exercicio: number): Observable<any> {
        const data = { numero, exercicio };
        return this.http.post(this.apiURL() + '/api/process/' + processId + '/associar-certificado', data, this.headers())
            .pipe(
                catchError(this.handleError)
            );
    }

    removeCertificado(processId: number, numero: number, exercicio: number): Observable<any> {
        const data = { numero, exercicio };
        return this.http.post(this.apiURL() + '/api/process/' + processId + '/remove-certificado', data, this.headers())
            .pipe(
                catchError(this.handleError)
            );
    }

    atualizarUrlCertificado(processId: number, codigoCertificadoPdf: number, urlCloudflare: string): Observable<any> {
        const data = { urlCloudflare };
        return this.http.post(this.apiURL() + '/api/process/' + processId + '/update-url-certificado/' + codigoCertificadoPdf , data, this.headers())
            .pipe(
                catchError(this.handleError)
            );
    }
}
